import { type FC, useMemo } from 'react';
import {
    HStack,
    IconDesktop,
    IconPhone,
    Sidebar,
    SidebarItem,
    SidebarSection,
    Text,
    VStack,
} from '@direct-frontend/components';
import { matchPath, useLocation } from '@direct-frontend/modules-router';
import { expandDesktopSectionsForMobile } from './lib/expandDesktopSectionsForMobile';
import { useAlwaysShow, useNeverNotify } from './lib/menuItemHooks';
import { type DashboardSidebarProps, type MenuItem, type MenuSection } from './lib/types';
import styles from './DashboardSidebar@mobile.module.css';

const DashboardSidebar = (props: DashboardSidebarProps) => {
    const { menuSections, isOpen = false, onToggleIsOpen } = props;

    const location = useLocation();
    const locationPathname = location.pathname;

    const mobileSections = useMemo(
        () =>
            menuSections
                .map((section) => [...section.filter((item) => item.isMobileSupported)])
                .filter((section) => section.length > 0),
        [menuSections]
    );

    const desktopSections = useMemo(
        () =>
            menuSections
                .map((section) => [...section.filter((item) => !item.isMobileSupported)])
                .filter((section) => section.length > 0),
        [menuSections]
    );

    const desktopRenderSections = useMemo(() => expandDesktopSectionsForMobile(desktopSections), [desktopSections]);

    return (
        <Sidebar
            isOpen={isOpen}
            onToggleIsOpen={(value: boolean) => onToggleIsOpen?.(value)}
            withoutCollapsed
            widthClassName={styles.sidebar}
            logo={{
                default: null,
            }}
            isNewMobileViewAvailable
        >
            <VStack gap="4">
                <HStack gap="8">
                    <IconPhone role="supplementary" color="gray" size="16" />
                    <Text role="supplementary" size="xs">
                        Разделы, адаптированные для телефона
                    </Text>
                </HStack>
                {mobileSections.length > 0 && (
                    <SidebarSection>
                        {mobileSections.map((section, index) => (
                            <RenderSection
                                key={index.toString()}
                                locationPathname={locationPathname}
                                section={section}
                            />
                        ))}
                    </SidebarSection>
                )}
            </VStack>
            <VStack gap="4" className={styles.desktopSections}>
                <HStack gap="8">
                    <IconDesktop role="supplementary" color="gray" size="16" />
                    <Text role="supplementary" size="xs">
                        Разделы ниже откроются в версии для компьютера
                    </Text>
                </HStack>
                {desktopRenderSections.map((section, index) => (
                    <RenderSection key={index.toString()} locationPathname={locationPathname} section={section} />
                ))}
            </VStack>
        </Sidebar>
    );
};

const RenderSection: FC<{ locationPathname: string; section: MenuSection }> = ({ locationPathname, section }) => {
    if (section.length === 0) {
        return null;
    }

    return (
        <SidebarSection>
            {section.map((menuItem) => (
                <RenderMenuItem key={menuItem.itemId} menuItem={menuItem} locationPathname={locationPathname} />
            ))}
        </SidebarSection>
    );
};

const RenderMenuItem: FC<{ menuItem: MenuItem; locationPathname: string }> = ({ menuItem, locationPathname }) => {
    const { routes, useShow = useAlwaysShow, useHasNotification = useNeverNotify, ...sidebarItemProps } = menuItem;
    const isShow = useShow();
    const hasNotification = useHasNotification();

    if (!isShow) {
        return null;
    }

    const isActive = routes.some((route) => matchPath(route, locationPathname));

    return <SidebarItem {...sidebarItemProps} isActive={isActive} hasNotification={hasNotification} />;
};

export default DashboardSidebar;
